import {
  CreditPlanFailureReason,
  CreditPlanResponseModel,
  CreditPlanStatus
} from '@/@types/client-api';
import { CreditPlanBlock } from '@/@types/content';
import { ICreditPlanBlockProps } from '@/components/blocks/CreditPlan/CreditPlanBlock';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import creditPlanEndpoints from '../api/client/creditPlan';
import libraryEndpoints from '../api/client/library';
import { setCookieValues, getCookieValues } from '@/utils/cookie-list-values';
import { CREDIT_PLANS_DEBUG_COOKIE_NAME } from '@/components/ui/CreditPlan/util/constants';
import {
  buildCreditPlanLicenseIdentifier,
  mapCreditPlanAlertMessage
} from '@/components/ui/CreditPlan/util/CreditPlanHelpers';

type ByJurisdiction = { jurisdiction: string };
type ByProcessId = { processId: string };
type ByLicenseIdentifier = { licenseIdentifier: string };
type UpsertPlanPayload = (ByJurisdiction | ByProcessId | ByLicenseIdentifier) & {
  plan: CreditPlanResponseModel;
};

export interface CreditPlanState {
  fetchStatus: 'pending' | 'completed' | 'failed';
  plans: Array<CreditPlanResponseModel>;
  content: ICreditPlanBlockProps | null;
  debugModeEnabled?: boolean;
  loadingStepIndexByPlanKey: Record<string, number>;
}

export type PlanLookup = Partial<ByJurisdiction & ByProcessId & ByLicenseIdentifier>;

export const buildCreditPlanStoreKey = ({
  processId,
  licenseIdentifier,
  jurisdiction
}: PlanLookup) => {
  if (processId) return `processId:${processId}`;
  if (licenseIdentifier) return `licenseIdentifier:${licenseIdentifier}`;
  if (jurisdiction) return `jurisdiction:${jurisdiction.toLowerCase()}`;
  return null;
};

const isTerminalStatus = (status?: CreditPlanStatus | null) =>
  status === CreditPlanStatus.COMPLETED || status === CreditPlanStatus.FAILED;

const getPlanLookupFromPlan = (plan?: CreditPlanResponseModel | null): PlanLookup => ({
  processId: plan?.processId ?? undefined,
  licenseIdentifier: plan?.licenseIdentifier ?? undefined,
  jurisdiction: plan?.jurisdiction ?? undefined
});

const getKnownPlanKeys = ({ processId, licenseIdentifier, jurisdiction }: PlanLookup) => {
  const keys = [
    processId ? `processId:${processId}` : null,
    licenseIdentifier ? `licenseIdentifier:${licenseIdentifier}` : null,
    jurisdiction ? `jurisdiction:${jurisdiction.toLowerCase()}` : null
  ].filter((value): value is string => !!value);

  return Array.from(new Set(keys));
};

function setPlanLoadingStepIndexForLookup(
  state: CreditPlanState,
  lookup: PlanLookup,
  index: number,
  stepCount?: number
) {
  const safeIndex = Math.max(0, index);
  const clampedIndex =
    !!stepCount && stepCount > 0 ? Math.min(safeIndex, stepCount - 1) : safeIndex;

  for (const knownKey of getKnownPlanKeys(lookup)) {
    state.loadingStepIndexByPlanKey[knownKey] = clampedIndex;
  }

  const key = buildCreditPlanStoreKey(lookup);
  if (key) {
    state.loadingStepIndexByPlanKey[key] = clampedIndex;
  }
}

function clearPlanLoadingStepIndexForLookup(state: CreditPlanState, lookup: PlanLookup) {
  const key = buildCreditPlanStoreKey(lookup);
  if (key) {
    delete state.loadingStepIndexByPlanKey[key];
  }

  for (const knownKey of getKnownPlanKeys(lookup)) {
    delete state.loadingStepIndexByPlanKey[knownKey];
  }
}

const creditPlanSlice = createSlice({
  name: 'creditPlan',
  initialState: {
    fetchStatus: 'pending',
    plans: [],
    content: null,
    debugModeEnabled: getCookieValues(CREDIT_PLANS_DEBUG_COOKIE_NAME)?.includes('true') ?? false,
    loadingStepIndexByPlanKey: {}
  } as CreditPlanState,
  reducers: {
    removePlan(state, action: PayloadAction<ByJurisdiction | ByProcessId | ByLicenseIdentifier>) {
      if ('processId' in action.payload) {
        const { processId: pid } = action.payload;
        state.plans = state.plans.filter(plan => plan.processId !== pid);
      } else if ('jurisdiction' in action.payload) {
        const { jurisdiction: juri } = action.payload;
        state.plans = state.plans.filter(plan => plan.jurisdiction !== juri);
      } else if ('licenseIdentifier' in action.payload) {
        const { licenseIdentifier: li } = action.payload;
        state.plans = state.plans.filter(plan => plan.licenseIdentifier !== li);
      }
    },
    upsertPlan(state, action: PayloadAction<UpsertPlanPayload>) {
      doUpsertPlan(state, action.payload);
    },
    setPlanLoadingStepIndex(
      state,
      action: PayloadAction<
        (ByJurisdiction | ByProcessId | ByLicenseIdentifier) & {
          index: number;
          stepCount?: number;
        }
      >
    ) {
      const { index, stepCount, ...lookup } = action.payload;
      setPlanLoadingStepIndexForLookup(state, lookup, index, stepCount);
    },
    incrementPlanLoadingStepIndex(
      state,
      action: PayloadAction<
        (ByJurisdiction | ByProcessId | ByLicenseIdentifier) & {
          stepCount?: number;
        }
      >
    ) {
      const knownKeys = getKnownPlanKeys(action.payload);
      const currentIndex = knownKeys.reduce((max, key) => {
        const value = state.loadingStepIndexByPlanKey[key];
        return value !== undefined ? Math.max(max, value) : max;
      }, 0);

      setPlanLoadingStepIndexForLookup(
        state,
        action.payload,
        currentIndex + 1,
        action.payload.stepCount
      );
    },
    clearPlanLoadingStepIndex(
      state,
      action: PayloadAction<ByJurisdiction | ByProcessId | ByLicenseIdentifier>
    ) {
      clearPlanLoadingStepIndexForLookup(state, action.payload);
    },
    updatePlanSavedItems(
      state,
      action: PayloadAction<{ jurisdiction: string; items: Record<string, boolean> }>
    ) {
      const { jurisdiction, items } = action.payload;
      const plan = state.plans.find(p => p.jurisdiction === jurisdiction);

      if (!plan) return;

      const results =
        plan.creditPlan?.creditProductGroups?.flatMap(g => g.searchResultItems ?? []) ?? [];
      for (const item of results) {
        const pk = item.variationPk;

        if (!pk || pk in items === false) continue;

        const isSaved = items[pk];
        item.isSavedToUserLibrary = isSaved;

        const idx = item.userList?.findIndex(x => x.pk === pk) ?? -1;
        if (idx > -1) {
          const userListItem = item.userList![idx];
          // if user has purchased it, archive it from the library
          if (userListItem.isPurchased) userListItem.isArchived = !isSaved;
          else userListItem.isFavorite = isSaved;
        } else {
          item.userList?.push({ isFavorite: isSaved, pk });
        }
      }
    },
    updateCreditPlanBlock(
      state,
      action: PayloadAction<{ override?: boolean; data: CreditPlanBlock }>
    ) {
      const { override, data } = action.payload;
      if (override || !state.content) state.content = data;
    },
    updateCreditPlanDebugMode(state, action: PayloadAction<boolean>) {
      state.debugModeEnabled = action.payload;
      updateDebugModeCookie(action.payload);
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      creditPlanEndpoints.endpoints.getAllCreditPlans.matchRejected,
      (state, _) => {
        state.fetchStatus = 'failed';
      }
    );
    // bulk load all plans into state upon fetch
    builder.addMatcher(
      creditPlanEndpoints.endpoints.getAllCreditPlans.matchFulfilled,
      (state, action) => {
        state.fetchStatus = 'completed';
        state.plans = action.payload.map(mapPlanToState);

        const activeKeys = new Set(
          state.plans
            .filter(plan => !isTerminalStatus(plan.status))
            .flatMap(plan => getKnownPlanKeys(getPlanLookupFromPlan(plan)))
        );

        state.loadingStepIndexByPlanKey = Object.fromEntries(
          Object.entries(state.loadingStepIndexByPlanKey).filter(([key]) => activeKeys.has(key))
        );
      }
    );
    // upsert single plan into state (used for real-time updates)
    builder.addMatcher(
      creditPlanEndpoints.endpoints.getCreditPlan.matchFulfilled,
      (state, action) => {
        doUpsertPlan(state, {
          processId: action.payload.processId!,
          jurisdiction: action.payload.jurisdiction!,
          licenseIdentifier: action.payload.licenseIdentifier!,
          plan: mapPlanToState(action.payload)
        });
      }
    );
    // upsert single plan into state (used for real-time updates) and set fetch status to failed if retrieval fails
    builder.addMatcher(
      creditPlanEndpoints.endpoints.getCreditPlan.matchRejected,
      (state, action) => {
        const plan = state.plans?.find(a => a.processId === action.meta.arg.originalArgs);
        doUpsertPlan(state, {
          processId: action.meta.arg.originalArgs!,
          plan: {
            ...plan,
            failureReason: CreditPlanFailureReason.UNABLE_TO_RETRIEVE_PLAN,
            status: CreditPlanStatus.FAILED
          }
        });
      }
    );
    // set initial status to started when creation begins
    builder.addMatcher(
      creditPlanEndpoints.endpoints.createCreditPlan.matchPending,
      (state, action) => {
        const licenseIdentifier = buildCreditPlanLicenseIdentifier(
          action.meta.arg.originalArgs?.indivSk ?? '',
          action.meta.arg.originalArgs?.jurisdiction ?? '',
          action.meta.arg.originalArgs?.flags ? action.meta.arg.originalArgs.flags[0] : undefined
        );
        doUpsertPlan(state, {
          jurisdiction: action.meta.arg.originalArgs?.jurisdiction ?? '',
          licenseIdentifier: licenseIdentifier,
          plan: {
            licenseIdentifier: licenseIdentifier,
            jurisdiction: action.meta.arg.originalArgs?.jurisdiction ?? '',
            status: CreditPlanStatus.STARTED
          }
        });

        setPlanLoadingStepIndexForLookup(
          state,
          {
            jurisdiction: action.meta.arg.originalArgs?.jurisdiction ?? '',
            licenseIdentifier
          },
          0
        );
      }
    );
    // upsert single plan into state upon creation (used for real-time updates)
    builder.addMatcher(
      creditPlanEndpoints.endpoints.createCreditPlan.matchFulfilled,
      (state, action) => {
        doUpsertPlan(state, {
          processId: action.payload.processId!,
          jurisdiction: action.payload.jurisdiction!,
          licenseIdentifier: action.payload.licenseIdentifier!,
          plan: mapPlanToState(action.payload)
        });
      }
    );
    // upsert single plan into state upon creation (used for real-time updates)
    builder.addMatcher(
      creditPlanEndpoints.endpoints.createCreditPlan.matchRejected,
      (state, action) => {
        doUpsertPlan(state, {
          jurisdiction: action.meta.arg.originalArgs.jurisdiction!,
          licenseIdentifier: buildCreditPlanLicenseIdentifier(
            action.meta.arg.originalArgs?.indivSk ?? '',
            action.meta.arg.originalArgs?.jurisdiction ?? '',
            action.meta.arg.originalArgs?.flags ? action.meta.arg.originalArgs.flags[0] : undefined
          ),
          plan: mapPlanToState(action.payload?.data as CreditPlanResponseModel)
        });
      }
    );
    // update saved state of credit plan items when a library item is added
    builder.addMatcher(libraryEndpoints.endpoints.addToLibrary.matchFulfilled, (state, action) => {
      const code = action.meta.arg.originalArgs;
      const results =
        state.plans?.flatMap(
          plan =>
            plan.creditPlan?.creditProductGroups?.flatMap(g => g.searchResultItems ?? []) ?? []
        ) ?? [];

      for (const item of results) {
        if (item.variationPk !== code) continue;

        item.isSavedToUserLibrary = true;

        const idx = item.userList?.findIndex(x => x.pk === code) ?? -1;
        if (idx > -1) {
          const userListItem = item.userList![idx];
          if (userListItem.isPurchased) userListItem.isArchived = false;
          else userListItem.isFavorite = true;
        } else {
          item.userList?.push({ isFavorite: true, pk: code });
        }
      }
    });
    // update saved state of credit plan items when a library item is removed
    builder.addMatcher(
      libraryEndpoints.endpoints.removeFromLibrary.matchFulfilled,
      (state, action) => {
        const code = action.meta.arg.originalArgs;
        const results =
          state.plans?.flatMap(
            plan =>
              plan.creditPlan?.creditProductGroups?.flatMap(g => g.searchResultItems ?? []) ?? []
          ) ?? [];

        for (const item of results) {
          if (item.variationPk !== code) continue;

          item.isSavedToUserLibrary = false;

          const idx = item.userList?.findIndex(x => x.pk === code) ?? -1;
          if (idx > -1) {
            const userListItem = item.userList![idx];
            if (userListItem.isPurchased) userListItem.isArchived = true;
            else userListItem.isFavorite = false;
          } else {
            item.userList?.push({ isFavorite: false, pk: code });
          }
        }
      }
    );

    // remove plan from state upon deletion
    builder.addMatcher(
      creditPlanEndpoints.endpoints.deleteCreditPlan.matchFulfilled,
      (state, action) => {
        const planId = action.meta.arg.originalArgs;
        clearPlanLoadingStepIndexForLookup(state, { processId: planId });
        state.plans = state.plans.filter(p => p.processId !== planId);
      }
    );
  }
});

function updateDebugModeCookie(enabled: boolean) {
  setCookieValues(CREDIT_PLANS_DEBUG_COOKIE_NAME, [`${enabled}`], 1, 30);
}

function doUpsertPlan(
  state: CreditPlanState,
  payload: PayloadAction<UpsertPlanPayload>['payload']
) {
  const foundIndex = [
    () =>
      'processId' in payload ? state.plans.findIndex(p => p.processId === payload.processId) : -1,
    () =>
      'licenseIdentifier' in payload
        ? state.plans.findIndex(p => p.licenseIdentifier === payload.licenseIdentifier)
        : -1,
    () =>
      'jurisdiction' in payload
        ? state.plans.findIndex(p => p.jurisdiction === payload.jurisdiction)
        : -1
  ].reduce((idx, fn) => (idx > -1 ? idx : fn()), -1);

  const previousPlan = foundIndex > -1 ? state.plans[foundIndex] : undefined;
  const previousLookup = getPlanLookupFromPlan(previousPlan);
  const incomingLookup: PlanLookup = {
    processId: payload.plan.processId ?? ('processId' in payload ? payload.processId : undefined),
    licenseIdentifier:
      payload.plan.licenseIdentifier ??
      ('licenseIdentifier' in payload ? payload.licenseIdentifier : undefined),
    jurisdiction:
      payload.plan.jurisdiction ?? ('jurisdiction' in payload ? payload.jurisdiction : undefined)
  };
  const previousKey = buildCreditPlanStoreKey(previousLookup);
  const nextKey = buildCreditPlanStoreKey(incomingLookup);

  if (foundIndex > -1) state.plans[foundIndex] = payload.plan;
  else state.plans.push(payload.plan);

  if (
    previousKey &&
    nextKey &&
    previousKey !== nextKey &&
    previousKey in state.loadingStepIndexByPlanKey &&
    !(nextKey in state.loadingStepIndexByPlanKey)
  ) {
    state.loadingStepIndexByPlanKey[nextKey] = state.loadingStepIndexByPlanKey[previousKey];
  }

  if (payload.plan.status === CreditPlanStatus.STARTED) {
    setPlanLoadingStepIndexForLookup(state, incomingLookup, 0);
  }

  if (isTerminalStatus(payload.plan.status)) {
    clearPlanLoadingStepIndexForLookup(state, incomingLookup);
  }

  const debugModeEnabled = !!payload.plan.debugInformation;
  state.debugModeEnabled = debugModeEnabled;
  updateDebugModeCookie(debugModeEnabled);
}

function mapPlanToState(response: CreditPlanResponseModel): CreditPlanResponseModel {
  return {
    ...response,
    alertMessages:
      response.alertMessages?.map(a => {
        return {
          copy: a.copy,
          style: mapCreditPlanAlertMessage(a.style as unknown as number)
        };
      }) ?? [],
    creditPlan: {
      creditBreakdown: {
        total: response.creditPlan?.creditBreakdown?.total ?? -1
      },
      creditProductGroups:
        response.creditPlan?.creditProductGroups?.map(g => ({
          heading: g.heading ?? '',
          totalTimeText: g.totalTimeText ?? '',
          searchResultItems: g.searchResultItems ?? []
        })) ?? []
    }
  };
}

export const {
  removePlan,
  upsertPlan,
  setPlanLoadingStepIndex,
  incrementPlanLoadingStepIndex,
  clearPlanLoadingStepIndex,
  updatePlanSavedItems,
  updateCreditPlanBlock,
  updateCreditPlanDebugMode
} = creditPlanSlice.actions;

export default creditPlanSlice.reducer;
